import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Flex,
  Box,
  SkeletonText,
  UnorderedList,
  ListItem,
  Icon,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  useLazyGetInterviewSummaryQuery,
  useGenerateSummaryMutation,
  useGetInterviewNoteMutation,
} from '@/store/query/interviews';
import { useRetry } from '@/hooks/use-retry';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { Check } from '@kula-ai/icons/dual-tone/Check';
import { EmptyResults } from '@/components/ats';
import { SVG_ASSERT_URL } from '@/constants/app-constants';
import { KText } from '@/ui-library/components/text';
import OptionHeader from './OptionHeader';
import { headerOptions } from '../utils/contant';

interface Topic {
  topic?: string;
  'topic:'?: string;
  key: string;
  summary: string | string[];
}

interface Question {
  question?: string;
  'question:'?: string;
  summary: string | string[];
}

interface Note {
  topics?: Topic[];
  questions?: Question[];
}

interface Summary {
  note_type: string;
  note: string;
  status: 'done' | 'create';
  id?: string;
}

interface SummaryValue {
  key: string;
  note: Note | null;
  status: 'done' | 'create';
}

interface Interview {
  intelligence_status: string;
}

interface TabOption {
  value: string;
  label: string;
  key: string;
}

interface InterviewSummaryProps {
  interview: Interview;
  interviewId?: string | undefined | number;
}

interface SummaryItemProps {
  title: string;
  content: string | string[];
  onCopy: (title: string, content: string | string[], type?: string) => void;
  type?: string;
}

const COPY_TIMEOUT = 2000;
const RETRY_OPTIONS = { factor: 1, retries: 10, minTimeout: 1000 };

const formatContent = (
  header: string,
  note: string | string[],
  type?: string
): string => {
  const formattedNote = Array.isArray(note)
    ? note.map((point) => `  • ${point}\n`).join('')
    : note;

  return type !== 'question'
    ? `${header}:\n${formattedNote}`
    : note?.length
    ? `${header}\n${formattedNote}`
    : '';
};

const SummaryItem = memo<SummaryItemProps>(
  ({ title, content, onCopy, type }) => {
    const [copyToggle, setCopyToggle] = useState(false);

    const handleCopy = useCallback(() => {
      setCopyToggle(true);
      onCopy(title, content, type);
      setTimeout(() => setCopyToggle(false), COPY_TIMEOUT);
    }, [title, content, onCopy, type]);

    return (
      <Flex
        flexDir="column"
        borderRadius="8px"
        border="1px solid transparent"
        p="20px"
        pos="relative"
        onMouseLeave={() => setCopyToggle(false)}
        _hover={{
          borderColor: 'border-color',
          boxShadow: 'drop-shadows-bottom',
          '> div > div': { visibility: 'visible', opacity: 1 },
        }}
      >
        <Flex alignItems="center" justifyContent="space-between" mb="14px">
          <KText
            bg="#EAEAFF"
            borderRadius="8px"
            color="#605AFB"
            width="fit-content"
            p="2px 8px"
            size="sm-medium"
          >
            {title}
          </KText>
          <Flex
            bg="#F5F5F5"
            p="6px"
            borderRadius="6px"
            cursor="pointer"
            visibility="hidden"
            opacity={0}
            onClick={handleCopy}
          >
            {copyToggle ? (
              <Icon as={Check} w="16px" h="16px" />
            ) : (
              <Icon as={Copy03} w="16px" h="16px" />
            )}
          </Flex>
        </Flex>
        {Array.isArray(content) ? (
          <UnorderedList>
            {content.map((point: string, index: number) => (
              <ListItem key={index} fontSize="14px">
                {point}
              </ListItem>
            ))}
          </UnorderedList>
        ) : (
          <KText size="sm-regular">{content}</KText>
        )}
      </Flex>
    );
  }
);

SummaryItem.displayName = 'SummaryItem';

const configSummary = (summaries: Summary[], tab: TabOption): SummaryValue => {
  const summary = summaries.find((summary) => summary.note_type === tab.key);

  if (summary?.status === 'done') {
    return {
      key: tab.key,
      note: summary?.note ? JSON.parse(summary?.note) : {},
      status: 'done',
    };
  }
  return { key: tab.key, note: null, status: 'create' };
};

export const InterviewSummary: React.FC<InterviewSummaryProps> = ({
  interview,
  interviewId: interviewIdProps,
}) => {
  const [tab, setTab] = useState<TabOption>(headerOptions[0]);
  const [summaryValue, setSummaryValue] = useState<SummaryValue | undefined>();
  const { interviewId: interviewIdFromParams } =
    useParams<{ interviewId: string }>();

  const interviewId = interviewIdProps || interviewIdFromParams;

  const [getInterviewSummary, { data, isFetching, isUninitialized }] =
    useLazyGetInterviewSummaryQuery();
  const [generateSummary, { data: generatedSummary }] =
    useGenerateSummaryMutation();
  const [getInterviewNote] = useGetInterviewNoteMutation();

  const { intelligence_status } = interview || {};

  // Show loader on initial load before query is triggered
  const isInitialLoading = isUninitialized || (isFetching && !data);

  const fetchInterviewSummary = useCallback(() => {
    if (interviewId) {
      getInterviewSummary({ interviewId });
    }
  }, [interviewId, getInterviewSummary]);

  useEffect(() => {
    fetchInterviewSummary();
  }, [fetchInterviewSummary]);

  useEffect(() => {
    if (data) {
      const value = configSummary(data, tab);
      if (value.status === 'create' && interviewId) {
        generateSummary({ interviewId, note_type: value.key });
      }
      setSummaryValue(value);
    }
  }, [data, tab, interviewId, generateSummary]);

  const getSummaryDetails = useCallback(async (): Promise<Summary> => {
    if (!generatedSummary?.id) {
      throw new Error('No summary ID available');
    }

    const result = await getInterviewNote({
      interviewId,
      noteId: generatedSummary.id,
    });

    if ('error' in result) {
      throw new Error('Failed to fetch interview note');
    }

    if (result.data?.status !== 'done') {
      throw new Error('Summary not ready');
    }

    return result.data;
  }, [getInterviewNote, interviewId, generatedSummary?.id]);

  const { inProgress, data: noteData } = useRetry({
    fn: getSummaryDetails,
    retryOptions: RETRY_OPTIONS,
    start: generatedSummary?.id,
  });

  useEffect(() => {
    if (noteData) {
      fetchInterviewSummary();
    }
  }, [noteData, fetchInterviewSummary]);

  const handleSummaryCopy = useCallback(
    (header: string, note: string | string[], type?: string) => {
      const formattedContent = formatContent(header, note, type);
      navigator.clipboard.writeText(formattedContent);
    },
    []
  );

  const handleCopy = useCallback(() => {
    if (!summaryValue?.note) return;

    const formattedContent =
      tab.key !== 'questions'
        ? summaryValue.note.topics
            ?.map((note) =>
              note?.summary?.length
                ? formatContent(
                    note?.topic || note?.['topic:'] || '',
                    note.summary
                  )
                : ''
            )
            .filter(Boolean)
            .join('\n')
        : summaryValue.note.questions
            ?.map(({ question, summary, ...rest }) =>
              summary?.length
                ? formatContent(
                    question || rest?.['question:'] || '',
                    summary,
                    'question'
                  )
                : ''
            )
            .filter(Boolean)
            .join('\n');

    navigator.clipboard.writeText(formattedContent);
  }, [summaryValue, tab.key]);

  const handleTabChange = useCallback((newTab: TabOption) => {
    setTab(newTab);
  }, []);

  const isCopyDisabled = useMemo(() => {
    return (
      (tab.key === 'questions' &&
        (summaryValue?.note?.questions?.length === 0 ||
          !summaryValue?.note?.questions)) ||
      (tab.key !== 'questions' &&
        (summaryValue?.note?.topics?.length === 0 ||
          !summaryValue?.note?.topics)) ||
      inProgress ||
      isFetching ||
      isInitialLoading
    );
  }, [summaryValue, tab.key, inProgress, isFetching, isInitialLoading]);

  const renderContent = useMemo(() => {
    if (inProgress || isFetching || isInitialLoading) {
      return (
        <Box flex="1" p="12px">
          <SkeletonText mt="4" noOfLines={10} spacing="3" skeletonHeight="2" />
        </Box>
      );
    }

    if (!summaryValue?.note) {
      return null;
    }

    return (
      <Flex h="100%" overflow="auto" gap="md" pt="md" flexDirection="column">
        {tab.key !== 'questions' ? (
          summaryValue.note.topics?.length ? (
            summaryValue.note.topics?.map((note) =>
              note?.summary?.length ? (
                <SummaryItem
                  key={note.key}
                  title={note?.topic || note?.['topic:'] || ''}
                  content={note.summary}
                  onCopy={handleSummaryCopy}
                />
              ) : null
            )
          ) : (
            <EmptyResults
              image={`${SVG_ASSERT_URL}/ats/no-applications.svg`}
              wrapperProps={{ h: '100%' }}
              text="No summary found"
              iconProps={{ h: '140px', w: '142px', mb: '12px' }}
            />
          )
        ) : summaryValue.note.questions?.length ? (
          summaryValue.note.questions?.map(({ question, summary, ...rest }) =>
            summary?.length ? (
              <SummaryItem
                key={question}
                title={question || rest?.['question:'] || ''}
                content={summary}
                onCopy={handleSummaryCopy}
                type="question"
              />
            ) : null
          )
        ) : (
          <EmptyResults
            image={`${SVG_ASSERT_URL}/ats/no-applications.svg`}
            wrapperProps={{ h: '100%' }}
            text="No summary found"
            iconProps={{ h: '140px', w: '142px', mb: '12px' }}
          />
        )}
      </Flex>
    );
  }, [
    inProgress,
    isFetching,
    isInitialLoading,
    summaryValue,
    tab.key,
    handleSummaryCopy,
  ]);

  if (intelligence_status !== 'completed') {
    return (
      <EmptyResults
        image={`${SVG_ASSERT_URL}/ats/no-applications.svg`}
        wrapperProps={{ h: '100%' }}
        text="No summary found"
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  return (
    <Flex flexDir="column" height="100%" overflow="hidden" p="12px">
      <Box>
        <OptionHeader
          onChange={handleTabChange}
          headerOptions={headerOptions}
          clickToCopy={handleCopy}
          toShowCopy
          disableCopy={isCopyDisabled}
          initialValue={tab}
        />
      </Box>
      {renderContent}
    </Flex>
  );
};
